import React from 'react';

const TaxResidencyForm = ({ formData, handleChange }) => {
  return (
    <section className="border-b border-gray-100 pb-8 md:pb-12">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-gray-900 tracking-tight">
          <span className="block text-sm font-normal text-gray-500 mt-1">Step 2 of 3: Tax Residency Declaration</span>
        </h2>
        
        <div className="space-y-6 md:space-y-8">
          {/* Progress indicator */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-start">
            <div className="bg-gray-200 text-gray-600 p-2 rounded-lg mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Tax Residency Information</h3>
              <p className="text-sm text-gray-600 mt-1">Please provide your tax residency details for compliance purposes.</p>
            </div>
          </div>

          {/* Main Form */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            {/* US Citizen */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Are you a U.S. citizen or resident for tax purposes? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-6">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isUSCitizen"
                    value="Yes"
                    checked={formData.isUSCitizen === 'Yes'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                    required
                  />
                  <span className="ml-2 text-gray-700">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="isUSCitizen"
                    value="No"
                    checked={formData.isUSCitizen === 'No'}
                    onChange={handleChange}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300"
                  />
                  <span className="ml-2 text-gray-700">No</span>
                </label>
              </div>
            </div>

            {/* Tax Residency Countries */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country/Countries of Tax Residency <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="taxResidencyCountries"
                value={formData.taxResidencyCountries}
                onChange={handleChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                placeholder="Enter country names separated by commas"
                required
              />
            </div>

            {/* TIN */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tax Identification Number (TIN) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="tin"
                value={formData.tin}
                onChange={handleChange}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                placeholder="Enter your TIN or equivalent"
                required
              />
            </div>

            {/* No TIN Reason */}
            {(!formData.tin || formData.tin === '') && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  If no TIN is available, provide reason
                </label>
                <input
                  type="text"
                  name="noTinReason"
                  value={formData.noTinReason}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                  placeholder="Explain why you don't have a TIN"
                />
              </div>
            )}

            {/* Tax Signature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Signature <span className="text-red-500">*</span>
              </label>
              <div className="bg-gray-50/50 p-4 rounded-lg border border-gray-100 mb-4">
                <div className="flex">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300 mr-2 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Instructions:</span> Sign a blank white paper with black ink, then upload a clear photo or scan.<br />
                    Your signature must match the one on your government ID and be fully visible.
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col w-full max-w-lg border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4">
                    <svg className="w-8 h-8 mb-4 text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      {formData.taxSignatureFile ? formData.taxSignatureFile.name : 'PNG, JPG, PDF (MAX. 5MB)'}
                    </p>
                  </div>
                  <input 
                    type="file" 
                    name="taxSignatureFile" 
                    onChange={handleChange} 
                    className="hidden" 
                    required 
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TaxResidencyForm;